import { Button } from 'lego-on-react';
import React, { useCallback, useLayoutEffect, useState } from 'react';

import i18n from '@yandex-int/i18n';
import { makeEmail } from 'client/utils/sharing';
import * as keyset from 'i18n/Access';
import block from 'propmods';

import { shareApi } from '../../../api';
import { useCurrentAliceApp } from '../../../hooks/useCurrentAliceApp';
import { useFetchingHandler } from '../../../hooks/useFetchingHandler';
import { UserEntity } from '../../../types/utils';
import { FormError } from '../../Form/FormError';
import { Loading } from '../../Loading/Loading';

import MinusIcon from '../../../icons/minus.svg';

import './UsersPage.scss';

const b = block('UsersPage');
const t = i18n(keyset);

interface UserProps {
    user: UserEntity;
    disabled?: boolean;
}

const User: React.FC<UserProps> = ({ user, disabled }) => {
    const skill = useCurrentAliceApp()!;
    const [isDeleted, setIsDeleted] = useState(false);

    const [deleteShareHandler, state] = useFetchingHandler({
        handler: shareApi.deleteUserShare,
        onLoad: () => setIsDeleted(true),
    });

    const deleteShare = useCallback(async () => {
        if (disabled) return;
        await deleteShareHandler({ userId: user.uid, skillId: skill.id }, skill.channel);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [skill.id, user.uid, disabled]);

    return (
        <>
            {isDeleted ? (
                <>
                    <div {...b('deletion-text')}>{t('users-deleted')}</div>
                    <div />
                </>
            ) : (
                <>
                    <div {...b('user-account')}>{makeEmail(user.login)}</div>
                    <div
                        {...b('delete-icon')}
                        onClick={disabled ? undefined : deleteShare}
                        title={t('deny-access')}
                        aria-disabled={disabled || undefined}
                    >
                        {state === 'loading' ? <Loading size="xxs" centered /> : <MinusIcon />}
                    </div>
                </>
            )}
        </>
    );
};

export const UsersPage: React.FC<{ disabled: boolean }> = ({ disabled }) => {
    const skill = useCurrentAliceApp()!;
    const [users, setUsers] = useState<UserEntity[]>([]);

    const [fetchUsersHandler, state] = useFetchingHandler({
        handler: shareApi.getUserShares,
    });

    const fetchUsers = useCallback(async () => {
        const users: UserEntity[] = await fetchUsersHandler({ skillId: skill.id }, skill.channel);

        setUsers(users);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [skill.id]);

    useLayoutEffect(() => {
        void fetchUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [skill.id]);

    return (
        <div {...b()}>
            <p>{t('users-descr')}</p>
            <div {...b('content')}>
                {(() => {
                    switch (state) {
                        case 'idle':
                            return users.length > 0 ? (
                                <div {...b('list')}>
                                    {users.map((user, i) => (
                                        <User user={user} key={i} disabled={disabled} />
                                    ))}
                                </div>
                            ) : (
                                t('users-only-you')
                            );
                        case 'loading':
                            return <Loading centered size="l" />;
                        case 'error':
                            return (
                                <div>
                                    <div {...b('error')}>
                                        <FormError>{t('error')}</FormError>
                                    </div>
                                    <Button size="s" theme="normal" onClick={fetchUsers} disabled={disabled}>
                                        {t('users-button-reload')}
                                    </Button>
                                </div>
                            );
                    }
                })()}
            </div>
        </div>
    );
};
